import { type FC } from "react";

import { TagIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId } from "../../../gql-generated/types";
import { Tags, TagsContainer } from "../../../lens-tags/components";
import { FormModal } from "../../../shared/components";
import { MAX_SNAPCHAT_TAGS } from "../../../submit/components/constants";
import { useLensChangeTracker, useTagsForm } from "../../hooks";
import { FieldLabel } from "../shared";

import { SidebarModalHeading, SidebarModalInfo } from "./LensSidebar.styles";
import { formCSS } from "./LensTags.styles";
import { SidebarFooterButtons } from "./SidebarFooterButtons";
import { SidebarPanel } from "./SidebarPanel";
import { LensTagsDescription, LensTagsTooltip, messages } from "./messages.generated";

interface CommunityTagsProps {
    disabled: boolean;
    tags: readonly string[];
    lensId: string;
}

export const CommunityTags: FC<CommunityTagsProps> = ({ disabled, tags, lensId }) => {
    const intl = useIntl();
    const { configs } = useFeatureGates();
    const isTrendingTagsEnabled = configs[CofConfigId.MyLensesTrendingTags].enabled;
    const { openTagsModal, tagsModalProps, applyTags, setTagsLoading, addTag, removeTag, tagsList } = useTagsForm(
        tags,
        lensId,
    );
    const { hasFieldChanged, tags: editedTags } = useLensChangeTracker(lensId);

    // Use edited tags (optimistic UI) with fallback to server tags
    const displayTags = (editedTags && editedTags.length > 0) ?? hasFieldChanged("tags") ? editedTags ?? [] : tags;

    return (
        <>
            <SidebarPanel
                dataTestIdPrefix="tags"
                label={
                    <FieldLabel hasChanges={hasFieldChanged("tags")}>
                        {intl.formatMessage(messages.lensTagsLabel, {
                            curr: displayTags.length,
                            limit: MAX_SNAPCHAT_TAGS,
                        })}
                    </FieldLabel>
                }
                actionLabel={intl.formatMessage(messages.lensTagsActionLabel)}
                description={<LensTagsDescription limit={MAX_SNAPCHAT_TAGS} />}
                tooltipContent={<LensTagsTooltip />}
                Icon={TagIcon}
                showModal={openTagsModal}
                disabled={disabled}
            >
                {displayTags.length > 0 ? <TagsContainer tagsList={displayTags} unremovable={true} /> : null}
            </SidebarPanel>
            <FormModal
                footer={
                    <SidebarFooterButtons
                        dataTestIdPrefix="tags"
                        onCancel={tagsModalProps.onCancel}
                        apply={applyTags}
                        applyLoading={setTagsLoading}
                    />
                }
                modalProps={tagsModalProps}
                {...(isTrendingTagsEnabled && {
                    width: 800,
                    formClassName: formCSS,
                })}
            >
                <SidebarModalHeading>Manage Lens Tags</SidebarModalHeading>
                <SidebarModalInfo>
                    Add up to eight tags. Descriptive tags help Snapchatters find your Lens!
                </SidebarModalInfo>
                <Tags addTag={addTag} removeTag={removeTag} tags={tagsList} placeholder="Tag your Lens" />
            </FormModal>
        </>
    );
};
